import {
  List,
  ListItem,
  Tag,
  Text,
  useColorModeValue,
  useMediaQuery,
} from "@chakra-ui/react";
import { Currency, formatPrice, toCurrency } from "@models/currency";
import { EventType, NFTEvent, NFTEventTier } from "@models/event";
import { DEFAULT_LANGUAGE, translate } from "@models/language";
import { Timezone } from "@models/timezone";
import { formatDate } from "@models/utils";
import { isEmpty } from "lodash";
import { useMemo } from "react";

const t = (key: string) => key;

const StyledTag = (props) => (
  <Tag
    fontWeight={500}
    fontSize="sm"
    borderRadius={4}
    bgColor="primary.300"
    textColor="#FFFFFF"
    mr={2}
    textTransform="capitalize"
    {...props}
  >
    {props.children}
  </Tag>
);

const TicketItem = ({
  tier,
  currency,
  timezone,
}: {
  tier: NFTEventTier;
  currency: Currency;
  timezone: Timezone;
}) => {
  const [isMobile] = useMediaQuery("(max-width: 640px)");
  const language = DEFAULT_LANGUAGE;

  const discount = useMemo(
    () => tier?.paymentConfig?.currentPriceDiscountPercentage || 0,
    [tier]
  );
  const price = useMemo(
    () => tier?.paymentConfig?.currentPrice.price || 0,
    [tier]
  );

  const priceName = useMemo(
    () =>
      translate(tier?.paymentConfig?.currentPrice.info, language).name || "",
    [tier]
  );

  const priceEnd = useMemo(
    () =>
      tier?.paymentConfig?.currentPrice.end
        ? `Until ${formatDate(
            tier.paymentConfig.currentPrice.end,
            timezone,
            "MMM dd"
          )}`
        : "",
    [tier, timezone]
  );

  const defaultPrice = useMemo(
    () => tier?.paymentConfig?.defaultPrice.price || 0,
    [tier]
  );

  const tierName = useMemo(
    () => translate(tier.info, language).name || tier.name,
    [language]
  );

  const whitelistOnly = useMemo(
    () => !isEmpty(tier?.paymentConfig?.whitelist),
    [tier]
  );

  const nftHolderOnly = useMemo(
    () => !isEmpty(tier?.paymentConfig?.nftConstraint?.collections),
    [tier]
  );

  const tokenHolderOnly = useMemo(
    () => !isEmpty(tier?.paymentConfig?.tokenConstraint?.contracts),
    [tier]
  );

  const soldOut = useMemo(
    () =>
      (tier?.paymentConfig?.max || -1) > 0
        ? (tier?.paymentConfig?.sold || 0) >= (tier?.paymentConfig?.max || -1)
        : false,
    [tier]
  );

  if (!tier.paymentConfig) return <></>;
  return (
    <ListItem w="full">
      <div className="flex flex-row w-full items-center">
        <div className="flex flex-row items-center flex-grow justify-start">
          <p className="sm:flex sm:flex-row items-center mr-2 font-base text-lg">
            <span
              className="flex flex-col mr-2 text-base font-medium"
              style={{ color: useColorModeValue("#444444", "#DEDEE0") }}
            >
              {tierName}
            </span>

            {tier.paymentConfig.inviteOnly && (
              <StyledTag
                bgColor="tags.inviteOnly.bg"
                textColor="tags.inviteOnly.text"
              >
                {t("Invite only")}
              </StyledTag>
            )}
            {discount > 0 && (
              <StyledTag
                bgColor="tags.discount.bg"
                textColor="tags.discount.text"
              >
                {`${discount.toFixed(0)}% Off ${!isMobile ? priceEnd : ""}`}
              </StyledTag>
            )}
            {(nftHolderOnly || tokenHolderOnly) && (
              <StyledTag
                bgColor="tags.holderOnly.bg"
                textColor="tags.holderOnly.text"
              >
                {t("Holder only")}
              </StyledTag>
            )}
            {whitelistOnly && (
              <StyledTag
                bgColor="tags.whitelistOnly.bg"
                textColor="tags.whitelistOnly.text"
              >
                {t("Whitelist Only")}
              </StyledTag>
            )}
          </p>
        </div>

        {/* Price */}
        <div className="flex flex-col flex-shrink-0 justify-end items-end">
          <Text fontWeight={500} fontSize="lg" color="neutral.300">
            {price === 0 ? (
              <span
                className="uppercase"
                style={{ color: useColorModeValue("#444444", "#9C7B64") }}
              >
                {t("free")}
              </span>
            ) : (
              <span className="justify-items-end">
                <div>
                  <span
                    className={`text-base mx-1 text-base ${
                      (discount > 0 || soldOut) && "line-through"
                    }`}
                    style={{ color: useColorModeValue("#444444", "#9C7B64") }}
                  >
                    {formatPrice(defaultPrice, currency)}
                  </span>
                  {discount > 0 && (
                    <span
                      className={`text-base mx-1 ${
                        soldOut ? "line-through" : "no-underline"
                      }`}
                      style={{ color: useColorModeValue("#444444", "#9C7B64") }}
                    >
                      {formatPrice(price, currency)}
                    </span>
                  )}
                </div>
              </span>
            )}
          </Text>
          {/* subscription */}
          <span className={`text-xs ${soldOut && "line-through"}`}>
            {t(tier.paymentConfig?.subscriptionInterval)}
          </span>

          <Text fontWeight={500} fontSize="lg" color="neutral.300">
            {soldOut && (
              <Tag
                bgColor="error"
                textColor="white"
                px={3}
                py={1}
                borderRadius="sm"
                textTransform="uppercase"
                mx={1}
              >
                Sold Out
              </Tag>
            )}
          </Text>
        </div>
      </div>
    </ListItem>
  );
};

const EventDetailTicketType = ({ event }: { event: NFTEvent }) => {
  let eventType;

  const tiers = useMemo(() => {
    if (!event) return [];
    return event.tiers.filter((tier) => tier.paymentConfig && !tier.hidden);
  }, [event]);

  const timezone = useMemo(() => {
    return event?.timezone || Timezone["Asia/Hong_Kong"];
  }, [event]);

  const currency = useMemo(() => {
    return toCurrency(event?.currency);
  }, [event]);

  if (isEmpty(tiers)) {
    return <></>;
  }

  switch (event.type) {
    case EventType.BENEFIT:
      eventType = "Benefit Type";
      break;
    case EventType.MEMBERSHIP:
      eventType = "Tiers";
      break;
    case EventType.TICKET:
    default:
      eventType = "Ticket Type";
      break;
  }

  return (
    <div className="px-0 w-full">
      <div className="flex justify-between items-center">
        <Text as="i" fontSize="2xl" color={useColorModeValue("", "white")}>
          {t(eventType)}
        </Text>
      </div>
      <List mt="4" spacing={6}>
        {tiers.map((tier, i) => (
          <TicketItem
            key={i}
            tier={tier}
            currency={currency}
            timezone={timezone}
          />
        ))}
      </List>
    </div>
  );
};

export default EventDetailTicketType;
